import {faPlus, faSpinnerThird, faUser} from '@fortawesome/pro-regular-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {Space, Tag, Tooltip} from 'antd';
import {uniqBy} from 'lodash';
import {useState} from 'react';

import {Locale} from '../../../../provider/locale/locale';
import {useSnackbar} from '../../../../provider/snackbar/snackbar-hook';
import {fetchReviewsReplyTagsSend} from '../../../../service/reviews/reviews';
import {useResponsibleUserSelect} from '../../../../service/reviews/reviews-responsible-users';
import {TagDataType} from '../../../../service/reviews/reviews-tags';
import {ReviewDataTagType, ReviewDataType, ReviewResponsibleUserType} from '../../../../service/reviews/reviews-type';
import {classNames} from '../../../../util/css';
import {ApiError} from '../../../../util/error';
import {useReviewStateHook} from '../reviews-state/reviews-state-provider';
import {TagsPopup} from '../tags-popup/tags-popup';

import * as styles from './tag-list.scss';

type PropsType = {
    refreshReview?: (reviewId: number) => Promise<unknown>;
    reviewId: number;
    deletedAt: string | null;
    tags: Array<ReviewDataTagType> | null;
    responsibleUser?: ReviewResponsibleUserType | null;
    updateReview: (reviewId: number, updateData: Partial<ReviewDataType>) => void;
};

export function TagList(props: PropsType): JSX.Element | null {
    const {reviewId, tags, deletedAt, responsibleUser, updateReview, refreshReview} = props;
    const tagList = tags || [];
    const {isShowAutotags} = useReviewStateHook();
    const {snackbar} = useSnackbar();
    const [isTagsPopupOpen, setIsTagsPopupOpen] = useState<boolean>(false);
    const [isCreating, setIsCreating] = useState<boolean>(false);
    const [removingTagIds, setRemovingTagIds] = useState<Array<number>>([]);
    const [isResponsibleUserLoading, setIsResponsibleUserLoading] = useState<boolean>(false);

    const {mutateAsync} = useResponsibleUserSelect(reviewId);

    async function sendTags(repliersTagsData: Array<ReviewDataTagType>) {
        const uniqueTagId = new Set(repliersTagsData.map((tag) => tag.id));

        const data = {
            tag_ids: [...uniqueTagId],
        };

        try {
            const response = await fetchReviewsReplyTagsSend(reviewId, data);

            if (response.status === 'OK') {
                await updateReview(reviewId, {tags: uniqBy(repliersTagsData, 'id')});

                if (refreshReview) {
                    await refreshReview(reviewId);
                }
            }

            setIsTagsPopupOpen(false);
            snackbar.success(<Locale stringKey="REVIEWS__REVIEW_ANSWER_CHANGE_TAG_SUCCESS" />);
        } catch (error: unknown) {
            snackbar.error(<Locale stringKey="REVIEWS__REVIEW_ANSWER_CHANGE_TAG_FAIL" />);

            if (!(error instanceof ApiError)) {
                throw error;
            }
        }
    }

    async function handleRemoveTag(id: number) {
        const newRemovingTagIds = [...removingTagIds, id];
        const newTagList = tagList.filter((tag) => !newRemovingTagIds.includes(tag.id));

        setRemovingTagIds(newRemovingTagIds);
        await sendTags([...new Set(newTagList)]);
        setRemovingTagIds((previousRemovingTagIds) => previousRemovingTagIds.filter((innerId) => innerId !== id));
    }

    async function handleApplyTags(newSelectedTagList: Array<TagDataType>) {
        const newTagIdsList = [
            ...tagList,
            ...newSelectedTagList.map((tag) => {
                return {id: tag.id, isAspect: false, title: tag.title, folderId: tag.folder?.id || null};
            }),
        ];

        setIsCreating(true);
        await sendTags(newTagIdsList);
        setIsCreating(false);
    }

    function onAddTagClick() {
        setIsTagsPopupOpen(true);
    }

    async function handleCloseResponsibleUserTag() {
        setIsResponsibleUserLoading(true);
        await mutateAsync(null, {
            onSuccess: () => {
                updateReview(reviewId, {responsibleUser: null});

                if (refreshReview) {
                    refreshReview(reviewId);
                }
            },
        });

        await setIsResponsibleUserLoading(false);
    }

    return (
        <ul className={classNames(styles.TagList)}>
            {responsibleUser && (
                <li className={styles.TagList_item}>
                    <Tag
                        className={classNames(
                            styles.TagList_itemTag,
                            styles.TagList_itemAutotag,
                            styles.TagList_responsibleUser
                        )}
                        closable={!isResponsibleUserLoading}
                        onClose={handleCloseResponsibleUserTag}
                    >
                        <FontAwesomeIcon icon={faUser} />
                        &nbsp;
                        <Locale stringKey="REVIEWS__RESPONSIBLE_USER__PLACEHOLDER" />
                        :&nbsp;{responsibleUser.fullName}
                        {isResponsibleUserLoading && (
                            <>
                                {' '}
                                <FontAwesomeIcon icon={faSpinnerThird} spin />
                            </>
                        )}
                    </Tag>
                </li>
            )}
            {tagList.map((tagData: ReviewDataTagType): JSX.Element | null => {
                const {id, title, isAspect} = tagData;
                const isTagRemoving = removingTagIds.includes(id);

                const tagClassNames = classNames(styles.TagList_itemTag, {
                    [styles.TagList_itemAutotag]: isAspect,
                    [styles.TagList_disabled]: Boolean(deletedAt),
                });

                if (isAspect && !isShowAutotags) {
                    return null;
                }

                return (
                    <li className={styles.TagList_item} key={id}>
                        <Tooltip title={isAspect ? <Locale stringKey="REVIEW__AUTO_TAG__TITLE" /> : ''}>
                            <Tag
                                className={tagClassNames}
                                closable={!isTagRemoving}
                                key={id}
                                onClose={() => handleRemoveTag(id)}
                            >
                                {title}
                                {isTagRemoving && (
                                    <>
                                        {' '}
                                        <FontAwesomeIcon icon={faSpinnerThird} spin />
                                    </>
                                )}
                            </Tag>
                        </Tooltip>
                    </li>
                );
            })}

            <li className={styles.TagList_item} key="new-tag-button">
                <Tag
                    className={classNames(styles.TagList_addTagButton, {[styles.TagList_disabled]: Boolean(deletedAt)})}
                    onClick={onAddTagClick}
                >
                    <Space size={4}>
                        <FontAwesomeIcon icon={faPlus} />
                        <Locale stringKey="REVIEWS__REVIEW__ADD_TAG" />
                    </Space>
                </Tag>

                <TagsPopup
                    isCreating={isCreating}
                    isOpen={isTagsPopupOpen}
                    onApply={handleApplyTags}
                    onClose={() => setIsTagsPopupOpen(false)}
                />
            </li>
        </ul>
    );
}
